<script>
  import ArrayEditor from '../common/ArrayEditor.svelte';
  import SuggestionsEditor from '../common/SuggestionsEditor.svelte';
  import PropsKeyValueEditor from '../dataset/PropsKeyValueEditor.svelte';
  
  export let question;
  export let updateWorkingQuestion;

  let config, isProps, propertyKey;
  
  // Question type configurations
  const questionTypeConfigs = {
    Text: {
      supportsSuggestions: true,
      supportsOther: false,
      supportsExtendable: true,
      supportsChoices: false,
      supportsSelection: false,
      supportsLocationCheck: true,
      defaultSuggestions: '__datasetNodeMatches__'
    },
    Select: {
      supportsSuggestions: true,
      supportsOther: true,
      supportsExtendable: false,
      supportsChoices: false,
      supportsSelection: false,
      supportsLocationCheck: false,
      defaultSuggestions: ['Option 1', 'Option 2', 'Option 3']
    },
    CheckBox: {
      supportsSuggestions: false,
      supportsOther: true,
      supportsExtendable: false,
      supportsChoices: true,
      supportsSelection: true,
      supportsLocationCheck: false,
      defaultSuggestions: null
    },
    Quant: {
      supportsSuggestions: false,
      supportsOther: false,
      supportsExtendable: false,
      supportsChoices: false,
      supportsSelection: false,
      supportsLocationCheck: false,
      defaultSuggestions: null
    },
    Map: {
      supportsSuggestions: false,
      supportsOther: false,
      supportsExtendable: false,
      supportsChoices: false,
      supportsSelection: false,
      supportsLocationCheck: false,
      defaultSuggestions: null
    },
    DateTime: {
      supportsSuggestions: false,
      supportsOther: false,
      supportsExtendable: false,
      supportsChoices: false,
      supportsSelection: false,
      supportsLocationCheck: false,
      defaultSuggestions: null
    }
  };
  
  // Pure functions for checking question state
  function isPropsQuestion() {
    if (!question.dataset || !Array.isArray(question.dataset)) return false;
    return question.dataset.some((item) => 
      item[0] === "selfProps" || item[0] === "nodeProps"
    ) && !question.dataset.some((item) => item[0] === "setSelf");
  }
  
  function getPropertyKey() {
    if (!isPropsQuestion()) return null;
    
    const dataset = question.dataset || [];
    const propsHandler = dataset.find(handler => 
      handler[0] === 'selfProps' || handler[0] === 'nodeProps'
    );
    
    if (propsHandler && propsHandler[propsHandler.length - 1]) {
      const props = propsHandler[propsHandler.length - 1];
      const answerProps = Object.entries(props).filter(([key, value]) => 
        value && (value.includes('__answer') || value === '__manyAnswers__')
      );
      
      return answerProps[0]?.[0] || `${question.kind.toLowerCase()}-prop`;
    }
    
    return `${question.kind.toLowerCase()}-prop`;
  }
  
  // Common functions for all question types
  function toggleExtendable() {
    if (question.extendable !== undefined) {
      updateWorkingQuestion('extendable', undefined);
      updateWorkingQuestion('maxAnswers', undefined);
    } else {
      updateWorkingQuestion('extendable', true);
      updateWorkingQuestion('maxAnswers', 10);
    }
  }
  
  function toggleOtherOption() {
    if (question.other !== undefined) {
      updateWorkingQuestion('other', undefined);
    } else {
      updateWorkingQuestion('other', 1);
    }
  }
  
  function addSelectionLimits() {
    updateWorkingQuestion('minSelected', 1);
    updateWorkingQuestion('maxSelected', 3);
  }
  
  function removeSelectionLimits() {
    updateWorkingQuestion('minSelected', undefined);
    updateWorkingQuestion('maxSelected', undefined);
  }
  
  function updateChoices(newChoices) {
    updateWorkingQuestion('choices', newChoices);
  }
  
  function toggleLocationCheck() {
    if (question.checkLocationName !== undefined) {
      updateWorkingQuestion('checkLocationName', undefined);
    } else {
      updateWorkingQuestion('checkLocationName', true);
    }
  }
  
  // Quant-specific functions
  function updateQuestionLabels(newLabels) {
    if (question.manyToMany) {
      updateWorkingQuestion('questionLabels', newLabels);
    } else {
      updateWorkingQuestion('fieldLabels', newLabels);
      updateWorkingQuestion('questionLabels', undefined);
    }
    
    syncDatasetWithLabels(newLabels);
  }
  
  function syncDatasetWithLabels(labels) {
    if (question.kind !== 'Quant') return;
    
    const dataset = [...(question.dataset || [])];
    const propsHandlerIndex = dataset.findIndex(handler => 
      handler[0] === 'selfProps' || handler[0] === 'nodeProps'
    );
    
    if (propsHandlerIndex !== -1) {
      const handler = [...dataset[propsHandlerIndex]];
      const existingProps = handler[handler.length - 1] || {};
      const newProps = {};
      
      // Preserve non-answer properties
      Object.keys(existingProps).forEach(key => {
        if (!key.includes('answer') && !key.match(/__answer\d+__/)) {
          newProps[key] = existingProps[key];
        }
      });
      
      const existingPropNames = getDatasetPropertyNames();
      
      labels.forEach((label, index) => {
        const propName = existingPropNames[index] || `prop-${index + 1}`;
        const answerRef = question.manyToMany ? '__manyAnswers__' : `__answer${index}__`;
        newProps[propName] = answerRef;
      });
      
      handler[handler.length - 1] = newProps;
      dataset[propsHandlerIndex] = handler;
      updateWorkingQuestion('dataset', dataset);
    }
  }
  
  function getDatasetPropertyNames() {
    const dataset = question.dataset || [];
    const propsHandler = dataset.find(handler => 
      handler[0] === 'selfProps' || handler[0] === 'nodeProps'
    );
    
    if (propsHandler && propsHandler[propsHandler.length - 1]) {
      const props = propsHandler[propsHandler.length - 1];
      return Object.keys(props).filter(key => 
        props[key] && (props[key].includes('__answer') || props[key] === '__manyAnswers__')
      );
    }
    
    return [];
  }
  
  function addQuestionLabel() {
    const newLabels = [...currentLabels, `Question ${currentLabels.length + 1}`];
    updateQuestionLabels(newLabels);
  }
  
  function removeQuestionLabel(index) {
    if (currentLabels.length > 1) {
      const newLabels = currentLabels.filter((_, i) => i !== index);
      updateQuestionLabels(newLabels);
    }
  }
  
  // DateTime-specific functions
  function toggleIncludeTime() {
    updateWorkingQuestion('includeTime', !question.includeTime);
  }
  
  function toggleMinDate() {
    if (question.minDate !== undefined) {
      updateWorkingQuestion('minDate', undefined);
    } else {
      updateWorkingQuestion('minDate', new Date().toISOString().split('T')[0]);
    }
  }
  
  function toggleMaxDate() {
    if (question.maxDate !== undefined) {
      updateWorkingQuestion('maxDate', undefined);
    } else {
      updateWorkingQuestion('maxDate', new Date().toISOString().split('T')[0]);
    }
  }
  
  function updateMinDate(value) {
    updateWorkingQuestion('minDate', value);
    if (question.maxDate && value && new Date(value) > new Date(question.maxDate)) {
      updateWorkingQuestion('maxDate', value);
    }
  }
  
  function updateMaxDate(value) {
    updateWorkingQuestion('maxDate', value);
    if (question.minDate && value && new Date(value) < new Date(question.minDate)) {
      updateWorkingQuestion('minDate', value);
    }
  }

  $: currentLabels = (() => {
  if (question.kind === 'Quant') {
    if (question.manyToMany) {
      return question.questionLabels || ['Question 1'];
    } else {
      return question.fieldLabels || ['Question 1'];
    }
  }
  return [];
})();
  
  // Reactive derived state - automatically updates when question changes
  $: question, config = questionTypeConfigs[question.kind] || {};
  $: question, isProps = isPropsQuestion();
  $: question, propertyKey = getPropertyKey();
  
</script>

<div class="unified-question-editor">
  <h4>{question.kind} Settings</h4>
  
  <!-- Property Key Configuration (for props questions) -->
  {#if isProps}
    <div class="props-section">
      <PropsKeyValueEditor 
        {question}
        {updateWorkingQuestion}
        propertyLabel="{question.kind} Properties"
        keyPlaceholder="{question.kind.toLowerCase()}-prop"
      />
    </div>
  {/if}
  
  <!-- Suggestions Configuration -->
  {#if config.supportsSuggestions}
    <SuggestionsEditor 
      {question}
      {updateWorkingQuestion}
      hasUnsavedChanges={true}
      questionType={question.kind}
    />
  {/if}
  
  <!-- Choices Configuration (CheckBox) -->
  {#if config.supportsChoices}
    <div class="choices-section">
      <label>Choices</label>
      <ArrayEditor 
        bind:items={question.choices}
        placeholder="Choice option"
        minItems={1}
        on:update={(e) => updateChoices(e.detail)}
      />
    </div>
  {/if}
  
  <!-- Selection Limits (CheckBox) -->
  {#if config.supportsSelection && !isProps}
    <div class="selection-section">
      {#if question.minSelected !== undefined || question.maxSelected !== undefined}
        <label>Selection Limits</label>
        <div class="selection-grid">
          <div class="field-wrapper">
            <label>Min Selected</label>
            <input 
              type="number"
              value={question.minSelected || 0}
              min="0"
              on:input={(e) => updateWorkingQuestion('minSelected', parseInt(e.target.value) || 0)}
            />
          </div>
          <div class="field-wrapper">
            <label>Max Selected</label>
            <input 
              type="number"
              value={question.maxSelected || 1}
              min="1"
              on:input={(e) => updateWorkingQuestion('maxSelected', parseInt(e.target.value) || 1)}
            />
          </div>
        </div>
        <button class="btn btn-warning btn-small" on:click={removeSelectionLimits}>
          Remove Selection Limits
        </button>
      {:else}
        <button class="btn btn-secondary" on:click={addSelectionLimits}>
          Add Selection Limits
        </button>
        <p class="field-help">Control how many options users can select</p>
      {/if}
    </div>
  {/if}
  
  <!-- Extendable Configuration (Text) -->
  {#if config.supportsExtendable && !isProps}
    <div class="extendable-section">
      {#if question.extendable !== undefined && question.extendable}
        <div class="extendable-active">
          <label>Multiple Fields Configuration</label>
          <div class="field-wrapper">
            <label>Maximum Answers</label>
            <input 
              type="number"
              value={question.maxAnswers || 10}
              min="1"
              max="50"
              on:input={(e) => updateWorkingQuestion('maxAnswers', parseInt(e.target.value) || 10)}
            />
          </div>
          <button class="btn btn-warning btn-small" on:click={toggleExtendable}>
            Disable Multiple Fields
          </button>
        </div>
      {:else}
        <button class="btn btn-secondary" on:click={toggleExtendable}>
          Enable Multiple Fields
        </button>
        <p class="field-help">Allow users to add multiple text inputs</p>
      {/if}
    </div>
  {/if}
  
  <!-- Other Option Configuration -->
  {#if config.supportsOther && !isProps}
    <div class="other-section">
      {#if question.other !== undefined}
        <label>Other Fields Count</label>
        <div class="other-controls">
          <input 
            type="number"
            value={question.other || 1}
            min="1"
            max="10"
            on:input={(e) => updateWorkingQuestion('other', parseInt(e.target.value) || 1)}
          />
          <button class="btn btn-warning btn-small" on:click={toggleOtherOption}>
            Remove "Other" Option
          </button>
        </div>
        <p class="field-help">Number of additional text fields for "other" responses</p>
      {:else}
        <button class="btn btn-secondary" on:click={toggleOtherOption}>
          Add "Other" Option
        </button>
        <p class="field-help">Allow users to enter custom responses</p>
      {/if}
    </div>
  {/if}
  
  <!-- Location Name Checking (Text) -->
  {#if config.supportsLocationCheck}
    <div class="location-section">
      <label class="checkbox-item">
        <input 
          type="checkbox"
          checked={question.checkLocationName === true}
          on:change={toggleLocationCheck}
        />
        Check Location Name
      </label>
      <p class="field-help">
        Validate entries against existing map location names for consistency
      </p>
    </div>
  {/if}
  
  <!-- Quant-specific Configuration -->
  {#if question.kind === 'Quant'}
    <div class="quant-section">
      <div class="quant-basic">
        <div class="field-wrapper">
          <label>Min Value</label>
          <input 
            type="number"
            value={question.minValue || 0}
            on:input={(e) => updateWorkingQuestion('minValue', parseInt(e.target.value) || 0)}
          />
        </div>
        <div class="field-wrapper">
          <label>Max Value</label>
          <input 
            type="number"
            value={question.maxValue || 10}
            on:input={(e) => updateWorkingQuestion('maxValue', parseInt(e.target.value) || 10)}
          />
        </div>
      </div>
      
      <div class="field-wrapper">
        <label class="checkbox-item">
          <input 
            type="checkbox"
            checked={question.percentage || false}
            on:change={(e) => updateWorkingQuestion('percentage', e.target.checked)}
          />
          Percentage Mode (all values sum to max)
        </label>
      </div>
      
      <!-- Question Labels - Fixed binding -->
      <div class="quant-labels">
        <label>Question Labels</label>
        <ArrayEditor 
          items={currentLabels}
          placeholder="Question label"
          minItems={1}
          on:update={(e) => updateQuestionLabels(e.detail)}
        />
      </div>
    </div>
  {/if}
  
  <!-- Map-specific Configuration -->
  {#if question.kind === 'Map'}
    <div class="map-section">
      <div class="field-wrapper">
        <label>Help Text</label>
        <input 
          type="text"
          value={question.helpText || ''}
          on:input={(e) => updateWorkingQuestion('helpText', e.target.value)}
          placeholder="e.g., Drag the pin to mark that place"
        />
      </div>
      
      <div class="field-wrapper">
        <label class="checkbox-item">
          <input 
            type="checkbox"
            checked={question.currentLocation || false}
            on:change={(e) => updateWorkingQuestion('currentLocation', e.target.checked)}
          />
          Allow Current Location
        </label>
      </div>
    </div>
  {/if}
  
  <!-- DateTime-specific Configuration -->
  {#if question.kind === 'DateTime'}
    <div class="datetime-section">
      <div class="field-wrapper">
        <label>Date/Time Type</label>
        <div class="datetime-options">
          <label class="radio-item">
            <input 
              type="radio"
              name="datetimeType"
              value="date"
              checked={question.includeTime !== true}
              on:change={() => updateWorkingQuestion('includeTime', false)}
            />
            Date Only
          </label>
          <label class="radio-item">
            <input 
              type="radio"
              name="datetimeType"
              value="datetime"
              checked={question.includeTime === true}
              on:change={() => updateWorkingQuestion('includeTime', true)}
            />
            Date and Time
          </label>
        </div>
      </div>
      
      <!-- Date Range Controls -->
      <div class="date-range-section">
        <label>Date Range</label>
        <div class="date-controls">
          <div class="date-control">
            <label class="checkbox-item">
              <input 
                type="checkbox"
                checked={question.minDate !== undefined}
                on:change={toggleMinDate}
              />
              Set minimum date
            </label>
            {#if question.minDate !== undefined}
              <input 
                type="date"
                value={question.minDate || ''}
                on:input={(e) => updateMinDate(e.target.value)}
              />
            {/if}
          </div>
          
          <div class="date-control">
            <label class="checkbox-item">
              <input 
                type="checkbox"
                checked={question.maxDate !== undefined}
                on:change={toggleMaxDate}
              />
              Set maximum date
            </label>
            {#if question.maxDate !== undefined}
              <input 
                type="date"
                value={question.maxDate || ''}
                on:input={(e) => updateMaxDate(e.target.value)}
              />
            {/if}
          </div>
        </div>
      </div>
      
      <div class="field-wrapper">
        <label class="checkbox-item">
          <input 
            type="checkbox"
            checked={question.defaultToToday === true}
            on:change={(e) => updateWorkingQuestion('defaultToToday', e.target.checked)}
          />
          Default to today's date
        </label>
      </div>
    </div>
  {/if}
</div>

<style lang="scss">
  .unified-question-editor {
    border: 4px solid #d4d3bc;
    border-radius: 0;
    padding: 15px;
    margin-bottom: 20px;
    background: #f3f2e3;
    
    h4 {
      margin-bottom: 15px;
      font-weight: bold;
      font-size: 0.9rem;
    }
  }
  
  .props-section,
  .choices-section,
  .selection-section,
  .extendable-section,
  .other-section,
  .location-section,
  .quant-section,
  .map-section,
  .datetime-section {
    margin-bottom: 20px;
    padding: 15px;
    border: 2px solid #e5e7eb;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.7);
    
    &:last-child {
      margin-bottom: 0;
    }
    
    > label {
      display: block;
      font-weight: bold;
      margin-bottom: 10px;
      font-size: 0.8rem;
      color: #374151;
    }
  }
  
  .selection-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 15px;
    margin-bottom: 10px;
  }
  
  .field-wrapper {
    margin-bottom: 15px;
    
    label {
      display: inline-flex;
      font-weight: bold;
      margin-bottom: 5px;
      font-size: 0.75rem;
      color: #464b4b;
    }
    
    input, select {
      width: 100%;
      padding: 8px 12px;
      border: 4px solid #d4d3bc;
      border-radius: 0;
      background: #f3f2e3;
      font-size: 0.75rem;
      
      &:focus {
        outline: none;
        border-color: #10b981;
        background: #fff;
      }
    }
  }
  
  .other-controls {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-bottom: 8px;
    
    input {
      flex: 0 0 80px;
    }
  }
  
  .checkbox-item {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 0.75rem;
    
    input {
      margin-right: 8px;
      width: auto;
    }
  }
  
  .radio-item {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 0.75rem;
    margin-bottom: 8px;
    
    input {
      margin-right: 8px;
      width: auto;
    }
  }
  
  .datetime-options {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .date-range-section {
    margin-bottom: 15px;
  }
  
  .date-controls {
    display: flex;
    flex-direction: column;
    gap: 15px;
  }
  
  .date-control {
    .checkbox-item {
      margin-bottom: 8px;
    }
    
    input[type="date"] {
      margin-top: 5px;
    }
  }
  
  .quant-basic {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 15px;
    margin-bottom: 15px;
  }
  
  .field-help {
    font-size: 11px;
    color: #6b7280;
    margin-top: 5px;
    font-style: italic;
  }
  
  .btn {
    padding: 6px 12px;
    border: none;
    border-radius: 0;
    cursor: pointer;
    font-size: 0.75rem;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: all 0.2s ease;
    
    &.btn-secondary {
      background: #10b981;
      color: white;
      
      &:hover {
        background: #059669;
      }
    }
    
    &.btn-warning {
      background: #f59e0b;
      color: white;
      
      &:hover {
        background: #d97706;
      }
    }
    
    &.btn-small {
      padding: 4px 8px;
      font-size: 12px;
    }
  }
</style>